import React from "react";
import {
  ActionIcon,
  CopyButton,
  Flex,
  Tooltip,
  Text,
  Menu,
  Box,
} from "@mantine/core";

import { IconCheck, IconCopy, IconDotsVertical } from "@tabler/icons-react";
import CustomButton from "../../components/mantineCustomComponents/CustomButton";
import PortfolioCollectionToggle from "../../components/PortfolioCollectionToggle";
import { formatHexStr } from "../formatHexStr";
import CustomAnchor from "../../components/mantineCustomComponents/CustomAnchor";
import { sanitizeDescription } from "../sanitizeDescription";

// Function for creating data
export const collectionPinnedTableData = (
  contentCollectionsPinned,
  setCollectionID,
  setIsPinned,
  setIsPublic,
  setOpenedModalPinned,
  setOpenedModalPublic,
  setOpenedModalDescription,
  form,
  userShortName,
  setPortfolio,
  setOpenedModalPortfolio,
  setPortfolioCollection,
  setOpenedModalPortfolioCollection,
  classesIcons,
  theme,
  tableClasses,
) => {
  if (!Array.isArray(contentCollectionsPinned)) {
    return [];
  }

  return contentCollectionsPinned.map((item) => ({
    name: (
      <CustomAnchor
        componentClasses="link link_black nav-link link-collection-name"
        text={
          item.name ? (
            <Text lineClamp={2} title={item.name}>
              {item.name}
            </Text>
          ) : (
            "n/a"
          )
        }
        href={`/verify/user-data/?user=${userShortName}&collection_name=${item.name}`}
      />
    ),
    nameValue: item.name,
    description: (
      <Flex align={theme.flex.align.center}>
        <Text lineClamp={2} title={item.description} component="div">
          <Box
            dangerouslySetInnerHTML={{
              __html: sanitizeDescription(item.description),
            }}
          />
        </Text>

        <CustomButton
          text={
            !item.description?.trim() || item.description === "<p></p>"
              ? "Add Description"
              : ""
          }
          componentClasses="link link_black"
          variant="transparent"
          type="button"
          onClick={() => {
            form.setValues({ collectionDescription: item.description });
            setCollectionID(item.id);
            setOpenedModalDescription(true);
          }}
        ></CustomButton>
      </Flex>
    ),
    descriptionValue: item.description,
    cid: (
      <Flex align={theme.flex.align.center} gap={theme.flex.gap.tiny}>
        <CustomAnchor
          title={item.cid}
          componentClasses="link link_black nav-link"
          text={formatHexStr(item.cid, 6, 4)}
          href={`/verify/user-data/?user=${userShortName}&collection_cid=${item.cid}`}
        />

        <CopyButton value={item.cid} timeout={2000}>
          {({ copied, copy }) => (
            <Tooltip
              label={copied ? "Copied" : "Copy"}
              className={classesIcons.copy}
              withArrow
              position="right"
            >
              <ActionIcon
                color={
                  copied
                    ? theme.customColors.actionIconFirst
                    : theme.customColors.actionIconSecond
                }
                variant="subtle"
                onClick={copy}
              >
                {copied ? (
                  <IconCheck className={classesIcons.width} />
                ) : (
                  <IconCopy className={classesIcons.width} />
                )}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      </Flex>
    ),
    cidValue: item.cid,
    is_public: <Box className="is-public">{item.is_public ? "Yes" : "No"}</Box>,
    is_publicValue: item.is_public,
    settings: (
      <Box className="settings">
        <Menu shadow="md" width="15em">
          <Menu.Target
            style={{
              cursor: "pointer",
            }}
          >
            <IconDotsVertical size={20} />
          </Menu.Target>
          <Menu.Dropdown>
            {item.name ? (
              <Menu.Item
                component="div"
                onClick={() => {
                  setIsPinned(false);
                  setCollectionID(item.id);
                  setOpenedModalPinned(true);
                }}
              >
                <CustomButton
                  text="Unpin"
                  componentClasses="link pin"
                  variant="transparent"
                  type="button"
                ></CustomButton>
              </Menu.Item>
            ) : (
              <Menu.Item
                component="div"
                onClick={() => {
                  setIsPinned(false);
                  setCollectionID(item.id);
                  setOpenedModalPinned(true);
                }}
              >
                <CustomButton
                  text="Unpin"
                  componentClasses="link linkWidth90"
                  variant="transparent"
                  type="button"
                ></CustomButton>
              </Menu.Item>
            )}
            <Menu.Item
              component="div"
              onClick={() => {
                setIsPublic(!item.is_public);
                setCollectionID(item.id);
                setOpenedModalPublic(true);
              }}
            >
              <Text className={tableClasses.link}>
                {" "}
                {item.is_public ? "Make Private" : "Make Public"}
              </Text>
            </Menu.Item>
            <Menu.Item
              component="div"
              onClick={() => {
                if (item.is_portfolio) {
                  setPortfolio(false);
                } else {
                  setPortfolio(true);
                }
                setCollectionID(item.id);
                setOpenedModalPortfolio(true);
              }}
            >
              <CustomButton
                text={
                  item.is_portfolio
                    ? "Disable Portfolio Dashboards"
                    : "Enable Portfolio Dashboards"
                }
                componentClasses="link"
                variant="transparent"
                type="button"
              />
            </Menu.Item>

            <Menu.Item
              component="div"
              onClick={() => {
                if (item.is_portfolio_collection) {
                  setPortfolioCollection(false);
                } else {
                  setPortfolioCollection(true);
                }
                setCollectionID(item.id);
                setOpenedModalPortfolioCollection(true);
              }}
            >
              <PortfolioCollectionToggle
                isPortfolioCollection={item.is_portfolio_collection}
                onToggle={() => {
                  setPortfolioCollection(!item.is_portfolio_collection);
                  setCollectionID(item.id);
                  setOpenedModalPortfolioCollection(true);
                }}
              />
            </Menu.Item>

            <Menu.Item
              component="div"
              onClick={() => {
                form.setValues({ collectionDescription: item.description });
                setCollectionID(item.id);
                setOpenedModalDescription(true);
              }}
            >
              <CustomButton
                text={item.description ? "Edit Description" : "Add Description"}
                componentClasses="link"
                variant="transparent"
                type="button"
              ></CustomButton>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Box>
    ),
  }));
};

// Function for creating columns
export const collectionPinnedTableColumns = () => [
  {
    accessorKey: "nameValue",
    header: "Name",
    enableSorting: true,
    size: "270",
    Cell: ({ row }) => row.original.name,
    sortingFn: (rowA, rowB) => {
      const nameA = String(rowA.original.nameValue ?? "")
        .toLowerCase()
        .trim();
      const nameB = String(rowB.original.nameValue ?? "")
        .toLowerCase()
        .trim();

      return nameA.localeCompare(nameB);
    },
  },
  {
    accessorKey: "descriptionValue",
    header: "Description",
    enableSorting: false, // sorting is disabled because there is complex html here
    size: "270",
    Cell: ({ row }) => row.original.description,
  },
  {
    accessorKey: "cidValue",
    header: "ID",
    size: "165",
    enableSorting: true,
    Cell: ({ row }) => row.original.cid,
    sortingFn: (rowA, rowB) => {
      const cidA = String(rowA.original.cidValue ?? "")
        .toLowerCase()
        .trim();
      const cidB = String(rowB.original.cidValue ?? "")
        .toLowerCase()
        .trim();

      return cidA.localeCompare(cidB);
    },
  },
  {
    accessorKey: "is_publicValue",
    header: "Public",
    size: "0",
    enableSorting: true,
    Cell: ({ row }) => row.original.is_public,
    sortingFn: (rowA, rowB) => {
      const publicA = Boolean(rowA.original.is_publicValue);
      const publicB = Boolean(rowB.original.is_publicValue);

      if (publicA === publicB) return 0;
      return publicA ? -1 : 1;
    },
  },

  {
    accessorKey: "settings",
    header: "Actions",
    enableSorting: false,
    size: "0",
    mantineTableBodyCellProps: {
      align: "center",
    },
  },
];

// Function for configuring the table
export const collectionPinnedTableOptions = (
  columns,
  data,
  faIcons,
  theme,
) => ({
  columns,
  data,
  icons: faIcons,
  paginationDisplayMode: "pages",
  enableFullScreenToggle: false,
  enableTopToolbar: false,
  enableBottomToolbar: true,
  enableColumnActions: false,
  initialState: { pagination: { pageIndex: 0, pageSize: 12 } },
  withColumnBorders: true,
  mantinePaginationProps: () => ({
    showRowsPerPage: false,
    color: theme.customBackgrounds.table,
    autoContrast: true,
    style: {
      paddingBottom: "1.75em",
    },
  }),
  mantineTableProps: {
    style: {
      border: "none",
      borderCollapse: "collapse",
    },
    className: "html-table html-table-custom html-table-pinned",
  },
  mantinePaperProps: {
    withBorder: false,
    shadow: "none",
    className: "html-table-custom-wrap",
  },
  mantineTableBodyCellProps: {
    style: {
      padding: "0.6875em 0.9375em 0.5625em 0.9375em",
      fontSize: "1.142em",
      wordBreak: "break-all",
    },
  },
  mantineTableHeadCellProps: {
    style: {
      padding: "0.55em 0.9375em 0.4375em 0.9375em",
      fontSize: "1.142em",
      fontFamily: theme.headings.fontFamily.bold,
    },
  },
  mantineTableBodyRowProps: () => ({
    style: {
      borderBottom: theme.customColors.tableBorder,
    },
  }),
  mantineTableHeadRowProps: () => ({
    style: {
      backgroundColor: theme.customBackgrounds.table,
    },
  }),
});
